import * as React from 'react';

import { NavSingleItemDisplayV2, isCourseFrameEnabled } from '@coursera-web/course-frame';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import { useLocation } from 'js/lib/useRouter';

import CourseStore from 'bundles/in-course-stores/stores/CourseStore';
import NavSingleItemDisplay from 'bundles/learner-progress/components/item/nav/private/NavSingleItemDisplay';
import type { Item } from 'bundles/learner-progress/types/Item';
import { getReviewPartFromSplitPeer, getSubmitPartFromPeer } from 'bundles/learner-progress/utils/Item';
import { canShowDistinctAvailabilityReviewDates } from 'bundles/learner-progress/utils/canShowDistinctAvailabilityReviewDates';
import { useCourseAiGradingContext } from 'bundles/peer/components/ai-grading/CourseAiEligibilityProvider';

type PropsFromCaller = {
  computedItem: Item;
  highlighted?: boolean;
  trackingName?: string;
  openInNewWindow?: boolean;
  iconSize?: number;
};

export type PropsFromCourseStore = {
  partnerIds?: number[];
};

export type Props = PropsFromCaller & PropsFromCourseStore;

export const NavPeerReviewItemDisplay = ({
  computedItem,
  highlighted,
  trackingName,
  openInNewWindow,
  iconSize,
}: Props) => {
  const { pathname } = useLocation();
  const { partnerIds } = useFluxibleStore(
    CourseStore,
    (courseStore): PropsFromCourseStore => ({
      partnerIds: courseStore.getMetadata()?.get('partnerIds'),
    })
  );

  const showDistinctAvailabilityReviewDates = canShowDistinctAvailabilityReviewDates(partnerIds);
  const reviewItem = getReviewPartFromSplitPeer(computedItem, showDistinctAvailabilityReviewDates);
  const submitItem = getSubmitPartFromPeer(computedItem);
  /* eslint-disable no-warning-comments */
  // HACK: To handle cases where we want to change display
  // for assignment and review parts for peer review depending on which page is
  // being accessed, we directly check the current path name.
  // This is to highlight the review part in nav on the review part page.
  /* eslint-enable no-warning-comments */
  const shouldHighlightReviewItem = reviewItem && reviewItem.resourcePath === pathname;

  const { getItemFullyAiGradingEligibility, isLoading } = useCourseAiGradingContext();
  const isFullyAiGraded = getItemFullyAiGradingEligibility(computedItem.id);
  const courseFrameEnabled = isCourseFrameEnabled();
  return (
    <span className="nostyle">
      {courseFrameEnabled ? (
        <NavSingleItemDisplayV2 computedItem={submitItem} highlighted={highlighted && !shouldHighlightReviewItem} />
      ) : (
        <NavSingleItemDisplay
          computedItem={submitItem}
          highlighted={highlighted && !shouldHighlightReviewItem}
          trackingName={trackingName}
          openInNewWindow={openInNewWindow}
          iconSize={iconSize}
        />
      )}
      {!isLoading &&
        !isFullyAiGraded &&
        reviewItem &&
        (courseFrameEnabled ? (
          <NavSingleItemDisplayV2 computedItem={reviewItem} highlighted={highlighted && shouldHighlightReviewItem} />
        ) : (
          <NavSingleItemDisplay
            computedItem={reviewItem}
            highlighted={highlighted && shouldHighlightReviewItem}
            trackingName={trackingName}
            openInNewWindow={openInNewWindow}
            iconSize={iconSize}
            showDistinctAvailabilityReviewDates={showDistinctAvailabilityReviewDates}
          />
        ))}
    </span>
  );
};

export default NavPeerReviewItemDisplay;
